/* =====================================================================
 * guideOutline — 가이드 markdown 을 "도입 문단 / 본문" 으로 나누고
 * `##` 제목에서 페이지 목차를 뽑는다.
 *
 * 절 id 는 공유 주소(`/user-guide?doc=agent#first-chat`)에 그대로 쓰이므로
 * 문서에서 직접 정한다: `## 첫 대화 시작하기 {#first-chat}`. 한글 제목을 자동
 * slug 로 바꾸면 주소가 `%EC%B2%AB…` 로 인코딩되고 조사 하나만 고쳐도 링크가
 * 깨진다. 표기가 없는 제목은 순번(`section-N`)으로 떨어진다.
 *
 * id 는 렌더 전에 한 번 계산하고 제목 컴포넌트는 텍스트로 찾아 쓴다 — 렌더
 * 중에 카운터를 세면 StrictMode 이중 렌더에서 번호가 밀린다.
 * ===================================================================== */

export interface GuideHeading {
  readonly id: string;
  readonly text: string;
  /** 이 절 본문(소제목 포함)의 평문. 목차 검색이 기능 이름으로도 절을 찾게 한다. */
  readonly searchText: string;
}

export interface GuideImage {
  readonly src: string;
  readonly alt: string;
  readonly caption?: string;
}

export interface GuideOutline {
  /** 첫 `##` 앞의 도입부. 목차 박스보다 위에 그린다. */
  readonly intro: string;
  /** 첫 `##` 부터 끝까지. */
  readonly content: string;
  readonly headings: readonly GuideHeading[];
  /** 본문에 나오는 캡처를 순서대로. 확대 보기에서 앞뒤로 넘길 때 쓴다. */
  readonly images: readonly GuideImage[];
}

const FENCE = /^\s*(```|~~~)/;
const IMAGE = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
const H2 = /^##\s+(.+?)\s*#*\s*$/;
const ANCHOR = /\s*\{#([a-z0-9][a-z0-9-]*)\}\s*$/;

/** 목차·id 대조용 평문. 굵게·코드·링크 표기를 걷어낸다. */
export function plainHeadingText(raw: string): string {
  return raw
    .replace(ANCHOR, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`]/g, '')
    .trim();
}

export function buildGuideOutline(markdown: string): GuideOutline {
  const lines = markdown.split('\n');
  const starts: Array<{ line: number; text: string; anchor?: string }> = [];
  let inFence = false;

  lines.forEach((line, index) => {
    if (FENCE.test(line)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;
    const match = H2.exec(line);
    if (!match) return;
    starts.push({ line: index, text: plainHeadingText(match[1]), anchor: ANCHOR.exec(match[1])?.[1] });
    /* 렌더할 본문에서는 `{#…}` 표기를 지운다. */
    lines[index] = line.replace(ANCHOR, '');
  });

  const headings = starts.map((start, i) => {
    const end = starts[i + 1]?.line ?? lines.length;
    return {
      id: start.anchor ?? `section-${i + 1}`,
      text: start.text,
      searchText: plainBodyText(lines.slice(start.line + 1, end).join('\n')),
    };
  });

  const images = extractGuideImages(markdown);
  if (starts.length === 0) {
    return { intro: markdown.trim(), content: '', headings, images };
  }
  return {
    intro: lines.slice(0, starts[0].line).join('\n').trim(),
    content: lines.slice(starts[0].line).join('\n'),
    headings,
    images,
  };
}

/** 검색용 평문. 이미지·링크·강조·표·목록 표기를 걷어낸다. */
function plainBodyText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s*(#{1,6}|>|\[!\w+\]|[-*]|\d+\.)\s*/gm, '')
    .replace(/[*_`|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extractGuideImages(markdown: string): GuideImage[] {
  return Array.from(markdown.matchAll(IMAGE), ([, alt, src, caption]) => ({
    src,
    alt,
    ...(caption ? { caption } : {}),
  }));
}
